import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Tag, Gavel, ArrowLeftRight, Heart, Share2, BookOpen, CheckCircle } from 'lucide-react';
import { useCard, useCardListings, useCardCollectionStatus } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import { formatPrice } from '@/utils';
import AddToCollectionModal from '@/components/AddToCollectionModal';
import CardImage from '@/components/CardImage';
// import PriceComparison from '@/components/PriceComparison'; // TODO: re-enable when price sync goes live
import QuickListingModal from '@/components/QuickListingModal';
import SEO from '@/components/SEO';

const RARITY_COLORS: Record<string, string> = {
  'Common': 'bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300',
  'Uncommon': 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  'Rare': 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  'Ultra Rare': 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  'Secret Rare': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
};

export default function CardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuthStore();
  const [showAddModal, setShowAddModal]   = useState(false);
  const [quickListItem, setQuickListItem] = useState<any | null>(null);

  const { data: card, isLoading: cardLoading } = useCard(id!);
  const { data: listings, isLoading: listingsLoading } = useCardListings(id!);
  const { data: collectionItems = [] } = useCardCollectionStatus(id ?? '');

  const inCollection = (collectionItems as any[]).length > 0;
  const totalOwned   = (collectionItems as any[]).reduce((s: number, i: any) => s + i.quantity, 0);

  // User-uploaded image only: own collection photo → any listing photo → placeholder
  const collectionImage  = (collectionItems as any[]).find((i) => i.image_urls?.length)?.image_urls?.[0];
  const listingImage     = (listings as any[])?.find((l) => l.image_urls?.length)?.image_urls?.[0];
  const displayImage     = collectionImage ?? listingImage ?? null;

  // Shape a collection item suitable for QuickListingModal (which expects a nested `card` object)
  const buildQuickListItem = (raw: any) => ({
    ...raw,
    listing_image_urls: raw.image_urls ?? [],
    card: {
      name:         raw.card_name        ?? card?.name,
      set_name:     raw.card_set_name    ?? card?.set_name,
      game:         raw.card_game        ?? card?.game,
      image_url:    raw.card_image_url   ?? card?.image_url ?? null,
      market_price: raw.card_market_price != null ? Number(raw.card_market_price) : card?.market_price ?? null,
    },
  });

  if (cardLoading) {
    return (
      <div className="container-custom py-12">
        <div className="skeleton h-8 w-32 rounded mb-8" />
        <div className="grid md:grid-cols-2 gap-10">
          <div className="skeleton aspect-[3/4] rounded-2xl" />
          <div className="space-y-4">
            <div className="skeleton h-10 w-3/4 rounded" />
            <div className="skeleton h-6 w-1/2 rounded" />
            <div className="skeleton h-32 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="container-custom py-24 text-center">
        <p className="text-5xl mb-4">🃏</p>
        <h2 className="text-2xl font-bold mb-2">Card not found</h2>
        <Link to="/explore" className="text-brand-600 dark:text-brand-400 hover:underline">Back to Explore</Link>
      </div>
    );
  }

  const rarityClass = card.rarity ? (RARITY_COLORS[card.rarity] ?? 'bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300') : 'bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300';

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        title={card.name}
        description={`${card.name} — ${card.set_name ?? ''}${card.rarity ? ` · ${card.rarity}` : ''}. View price history, listings, and add to your collection on Open Card Vault.`}
        url={`/cards/${card.id}`}
        image={card.image_url ?? undefined}
      />
      <div className="container-custom py-8">
        {/* Back */}
        <Link to="/explore" className="inline-flex items-center gap-2 text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 mb-6 font-medium">
          <ArrowLeft className="h-4 w-4" /> Back to Explore
        </Link>

        {/* Card Header */}
        <div className="grid md:grid-cols-2 gap-10 mb-12">
          {/* Image */}
          <div className="flex justify-center">
            <div className="w-full max-w-xs">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-zinc-800 to-gray-200">
                <CardImage
                  src={displayImage}
                  alt={card.name}
                  cardName={card.name}
                  cardNumber={card.card_number}
                  game={card.game}
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 ${rarityClass}`}>
                  {card.rarity}
                </span>
                <h1 className="text-4xl font-display font-bold">{card.name}</h1>
                <p className="text-gray-500 dark:text-zinc-500 mt-1">{card.set_name} · #{card.number}</p>
              </div>
              <div className="flex gap-2">
                <button className="p-2 rounded-xl border border-gray-200 dark:border-zinc-800 hover:border-brand-400 hover:text-brand-600 dark:text-brand-400 transition-colors">
                  <Heart className="h-5 w-5" />
                </button>
                <button className="p-2 rounded-xl border border-gray-200 dark:border-zinc-800 hover:border-brand-400 hover:text-brand-600 dark:text-brand-400 transition-colors">
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Type / Attributes */}
            {card.type && (
              <div className="flex gap-2 mt-3 mb-5">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-medium">{card.type}</span>
                {card.subtype && (
                  <span className="px-3 py-1 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 rounded-full text-sm font-medium">{card.subtype}</span>
                )}
              </div>
            )}


            {/* Price Comparison — TODO: re-enable when price sync goes live */}
            {/* <PriceComparison
              marketPrice={card.market_price}
              cardmarketPrice={card.cardmarket_price}
              foilPrice={card.foil_price}
              priceSource={card.price_source}
              lastPriceUpdate={card.last_price_update}
            /> */}

            {/* Stats grid */}
            {(card.hp || card.attack || card.defense) && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                {card.hp && (
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 border border-gray-200 dark:border-zinc-800 text-center">
                    <p className="text-xs text-gray-500 dark:text-zinc-500 mb-1">HP</p>
                    <p className="font-bold text-lg text-red-500">{card.hp}</p>
                  </div>
                )}
                {card.attack && (
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 border border-gray-200 dark:border-zinc-800 text-center">
                    <p className="text-xs text-gray-500 dark:text-zinc-500 mb-1">Attack</p>
                    <p className="font-bold text-lg text-orange-500">{card.attack}</p>
                  </div>
                )}
                {card.defense && (
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 border border-gray-200 dark:border-zinc-800 text-center">
                    <p className="text-xs text-gray-500 dark:text-zinc-500 mb-1">Defense</p>
                    <p className="font-bold text-lg text-blue-500">{card.defense}</p>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {card.description && (
              <p className="text-gray-600 dark:text-zinc-400 text-sm leading-relaxed">{card.description}</p>
            )}

            {/* Collection + Listing actions */}
            <div className="mt-6 flex flex-col gap-3">
              {isAuthenticated && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddModal(true)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                      inCollection
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:bg-emerald-900/40'
                        : 'bg-brand-600 text-white hover:bg-brand-700'
                    }`}
                  >
                    {inCollection ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        In Collection ({totalOwned})
                      </>
                    ) : (
                      <>
                        <BookOpen className="h-4 w-4" />
                        Add to Collection
                      </>
                    )}
                  </button>
                  {inCollection && (
                    <Link
                      to="/collection"
                      className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-zinc-800 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
                    >
                      View Collection
                    </Link>
                  )}
                </div>
              )}

              {inCollection ? (
                <button
                  onClick={() => setQuickListItem(buildQuickListItem((collectionItems as any[])[0]))}
                  className="btn-secondary w-full py-2.5"
                >
                  + List This Card for Sale
                </button>
              ) : (
                <Link
                  to="/listings/create"
                  state={{ cardId: card.id }}
                  className="btn-secondary w-full text-center block py-2.5"
                >
                  + List This Card for Sale
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Collection status strip (if user owns this card) */}
        {isAuthenticated && inCollection && (
          <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                You own {totalOwned} cop{totalOwned === 1 ? 'y' : 'ies'} of this card
              </p>
              <p className="text-xs text-emerald-600">
                {(collectionItems as any[]).map((i: any) => `${i.quantity}× ${i.condition}`).join(', ')}
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline flex-shrink-0"
            >
              Add more
            </button>
          </div>
        )}

        {/* Active Listings */}
        <section>
          <h2 className="text-2xl font-display font-bold mb-5">Listings for this Card</h2>

          {listingsLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
            </div>
          ) : listings?.length === 0 ? (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-dashed border-gray-300 dark:border-zinc-700 p-10 text-center">
              <p className="text-gray-500 dark:text-zinc-500 text-sm">No active listings for this card yet.</p>
              {inCollection ? (
                <button
                  onClick={() => setQuickListItem(buildQuickListItem((collectionItems as any[])[0]))}
                  className="text-brand-600 dark:text-brand-400 font-medium hover:underline text-sm mt-2 inline-block"
                >
                  Be the first to list it →
                </button>
              ) : (
                <Link to="/listings/create" state={{ cardId: card.id }} className="text-brand-600 dark:text-brand-400 font-medium hover:underline text-sm mt-2 inline-block">
                  Be the first to list it →
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {listings?.map((listing) => (
                <Link
                  key={listing.id}
                  to={`/listings/${listing.id}`}
                  className="card-hover bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-4 flex items-center gap-4 shadow-sm"
                >
                  {/* Type indicator */}
                  <div className={`p-2.5 rounded-xl ${
                    listing.type === 'auction' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-600' :
                    listing.type === 'trade' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600' :
                    'bg-green-100 dark:bg-green-900/40 text-green-600'
                  }`}>
                    {listing.type === 'auction' && <Gavel className="h-5 w-5" />}
                    {listing.type === 'trade' && <ArrowLeftRight className="h-5 w-5" />}
                    {listing.type === 'sale' && <Tag className="h-5 w-5" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{listing.seller?.username}</p>
                    <p className="text-xs text-gray-500 dark:text-zinc-500">{listing.condition}</p>
                  </div>

                  <div className="text-right">
                    {listing.type === 'auction' ? (
                      <p className="font-bold text-orange-600">{formatPrice(listing.current_bid ?? listing.starting_price)}</p>
                    ) : listing.type === 'trade' ? (
                      <p className="font-semibold text-purple-600 text-sm">Trade offer</p>
                    ) : (
                      <p className="font-bold text-brand-600 dark:text-brand-400">{formatPrice(listing.price)}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {showAddModal && card && (
        <AddToCollectionModal
          card={card}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {quickListItem && (
        <QuickListingModal
          item={quickListItem}
          onClose={() => setQuickListItem(null)}
        />
      )}
    </div>
  );
}